import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { decodeToken } from '../../utils/TokenUtils';

const initialState = {
	selectCenter: JSON.parse(localStorage.getItem('selectedCenter')) || null,
	isError: false,
	isLoading: true,
	isSuccess: false,
	message: '',
};

/**
 * @name getCenter
 * @description The select center thunk
 * @param {String} centerId The center id
 * @returns {Promise} The promise
 * @version 1.0.0
 *
 */
export const getCenter = createAsyncThunk<any, { centerId: string; userId?: string }>(
	'selectedCenter/getCenter',
	async (data: any, thunkAPI) => {
		const { centerId, userId } = data || {};
		if (!centerId) return null;
		try {
			const patchPromise = axios.patch(`/api/v1/users/selected-center/${centerId}`);
			const centersPromise = userId
				? axios.get(`/api/v1/users/centers/${userId}`).catch(() => null)
				: Promise.resolve(null);

			const [patchRes, centersRes] = await Promise.all([patchPromise, centersPromise]);

			// 1. Extraer desde la respuesta del patch si trae el usuario con centros
			const userPayload = patchRes?.data?.data || patchRes?.data?.user;
			if (userPayload?.centers && Array.isArray(userPayload.centers)) {
				const center = userPayload.centers.find(
					(c: any) => String(c?.centerId ?? c?.id) === String(centerId)
				);
				if (center) {
					return {
						centerId: String(center.centerId ?? center.id),
						name: center.name,
						role: center.role || center.UsersCenters?.role || 'admin',
					};
				}
			}

			// 2. Extraer desde la respuesta de centros del usuario
			const rawData = centersRes?.data?.data;
			const list = Array.isArray(rawData)
				? rawData
				: (rawData?.token ? decodeToken(rawData.token) : []);

			if (Array.isArray(list) && list.length > 0) {
				const center = list.find(
					(c: any) => String(c?.centerId ?? c?.id) === String(centerId)
				);
				if (center) {
					return {
						centerId: String(center.centerId ?? center.id),
						name: center.name,
						role: center.role || center.users?.[0]?.UsersCenters?.role || center.users?.[0]?.role || 'admin',
					};
				}
			}

			// 3. Fallback seguro
			return {
				centerId: String(centerId),
				name: 'Centro',
				role: 'admin',
			};
		} catch (error: any) {
			return thunkAPI.rejectWithValue(error?.response?.data?.message || error?.message || 'Error selecting center');
		}
	}
);

/**
 *
 * @name selectCenterSlice
 * @description The auth slice
 * @version 1.0.0
 *
 */
export const selectCenterSlice = createSlice({
	name: 'selectedCenter',
	initialState,
	reducers: {
		reset: () => initialState,
	},
	extraReducers: builder => {
		builder.addCase(getCenter.pending, state => {
			state.isLoading = true;
		});
		builder.addCase(getCenter.fulfilled, (state, action) => {
			state.isLoading = false;
			state.isSuccess = true;
			state.selectCenter = action.payload;
			// Guardar en local storage
			localStorage.setItem(
				'selectedCenter',
				JSON.stringify(action.payload),
			);
		});
		builder.addCase(getCenter.rejected, (state, action) => {
			state.isLoading = false;
			state.isError = true;
			state.message = String(action.payload || 'Error selecting center');
		});
	},
});

export const { reset } = selectCenterSlice.actions;
export default selectCenterSlice.reducer;
